/**
 * RunnerSilkAndNumbers component displays a horse's racing silk, form, cloth number,
 * and barrier number in race cards with responsive layout adjustments.
 */
import { Platform, StyleSheet, View } from "react-native";
import React from "react";
import RunnerSilk from "../../../../Racecard/RunnerSilk";
import RText from "../../../../RText/RText";
import { useResponsive } from "../../../../../hooks/useResponsive";
import Gap from "../../../../Gap";
import { Silk } from "../../../../../types/races/runner";

/**
 * Props for the RunnerSilkAndNumbers component.
 * @typedef {Object} RunnerSilkAndNumbersProps
 * @property {string} form - The horse's previous form/results as a string.
 * @property {number} [clothNumber] - The number displayed on the jockey's cloth.
 * @property {number} [barrierNumber] - The starting barrier/stall number.
 * @property {Silk} [silk] - The jockey's silk image data.
 * @property {Object} status - The current status of the runner (e.g., running, scratched, reserve).
 * @property {string} [status.state] - The state of the runner (e.g., "reserve", "scratched").
 */
type Props = {
  form: string;
  clothNumber?: number;
  barrierNumber?: number;
  silk?: Silk;
  status: any;
  textBadges?: boolean;
};

/**
 * Displays a horse's racing silk, form, cloth number, and barrier number.
 * Layout changes responsively based on screen size.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The rendered RunnerSilkAndNumbers component
 */
const RunnerSilkAndNumbers = ({
  form,
  clothNumber,
  barrierNumber,
  silk,
  status,
  textBadges = false,
}: Props) => {
  const { isSmallAndDown } = useResponsive();

  /**
   * Formats the horse's form for display.
   * Returns the most recent form figures (up to 6 characters) in correct order.
   *
   * @param {string} form - The horse's form string
   * @returns {string|undefined} The formatted form string or undefined if no form
   */
  const formattedForm = (form: string) => {
    if (!form) return;
    return form
      ?.split("")
      .reverse()
      .join("")
      .substring(0, 6)
      .split("")
      .reverse()
      .join("");
  };

  const fontSize = isSmallAndDown ? "sm" : "md";

  /**
   * Maps cloth numbers to display text based on runner status.
   * Adds 'R' prefix for reserves and shows 'NR' for non-runners.
   *
   * @param {number} clothNumber - The runner's cloth number
   * @returns {string|number|undefined} The formatted cloth number text or undefined
   */
  const clothNumberMap = (clothNumber: number | undefined) => {
    if (!clothNumber) return;
    if (status?.state === "reserve") return `R${clothNumber}`;
    if (status?.state === "scratched") return `NR`;
    if (!status?.state) return;
    return clothNumber;
  };

  let barrierFormPosition: number;
  if (textBadges) {
    barrierFormPosition = Platform.OS === "web" ? 23 : 28;
  } else {
    barrierFormPosition = Platform.OS === "web" ? 3 : 8;
  }
  return isSmallAndDown ? (
    <View style={[styles.center, styles.container, { marginBottom: 10 }]}>
      <View style={{ flexDirection: "row", width: "100%" }}>
        <View style={styles.center}>
          <View style={{ paddingRight: 5 }}>
            <RunnerSilk silk={silk?.press_association_file} />
          </View>
        </View>

        <View
          style={{
            flexDirection: isSmallAndDown ? "column" : "row",
            alignItems: "flex-end",
            justifyContent: "flex-end",
          }}
        >
          <RText size={fontSize} style={styles.alignRight} weight="bold">
            {clothNumberMap(clothNumber)}
          </RText>
          <Gap />
          {barrierNumber ? (
            <RText
              size={fontSize}
              style={[styles.alignRight, { top: barrierFormPosition }]}
            >
              ({barrierNumber})
            </RText>
          ) : (
            <RText> </RText>
          )}
        </View>
      </View>
      <View
        style={{
          width: "100%",
          top: barrierFormPosition,
        }}
      >
        <RText style={{ textAlign: "right" }}>{formattedForm(form)}</RText>
      </View>
    </View>
  ) : (
    <View style={[styles.center, styles.container]}>
      <View
        style={{
          flexDirection: "row",
          width: "100%",
        }}
      >
        <RunnerSilk silk={silk?.press_association_file} />
        {!isSmallAndDown && <Gap size="small" />}
        <View>
          <View
            style={{
              flexDirection: isSmallAndDown ? "column" : "row",
              justifyContent: "flex-start",
            }}
          >
            <RText size={fontSize} weight="bold">
              {clothNumberMap(clothNumber)}
            </RText>
            <Gap size="xsmall" />
            {barrierNumber ? (
              <RText size={fontSize}>({barrierNumber})</RText>
            ) : null}
          </View>
          <RText>{formattedForm(form)}</RText>
        </View>
      </View>
    </View>
  );
};

export default RunnerSilkAndNumbers;

const styles = StyleSheet.create({
  container: {
    minWidth: 55,
  },
  center: {
    alignItems: "center",
  },
  alignRight: {
    alignItems: "flex-end",
  },
});
